import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Clock, Save, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function VCRStage1({ report, onNext, onSave, onClose }) {
  const [formData, setFormData] = useState({
    driver_id: '',
    incident_date: '',
    incident_time: '',
    employee_name: '',
    pay_number: '',
    investigating_manager: '',
    date_first_reported: '',
    driver_type: '',
    injury_harm: '',
    attention_received: '',
    potential_absence: '',
    job_title_duty: '',
    adm_area: '',
    voc: '',
    initial_collision_type: '',
    what_damaged: '',
    manoeuvre: '',
    vehicle_collision_point: '',
    vehicle_type: '',
    location_name: '',
    location_type: '',
    weather_conditions: '',
    light_conditions: '',
    initial_statement: ''
  });

  const [drivers, setDrivers] = useState([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [isOverdue, setIsOverdue] = useState(false);
  const dropdownRef = useRef(null);

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/drivers`, getAuthHeaders());
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to load drivers');
    }
  };

  useEffect(() => {
    fetchDrivers();
    if (report.stage1_data) {
      setFormData(report.stage1_data);
      if (report.stage1_data.employee_name) {
        setDriverSearch(report.stage1_data.employee_name);
      }
    }
  }, [report]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDriverDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDriverChange = (driverId) => {
    const selectedDriver = drivers.find(d => d.id === driverId);
    if (selectedDriver) {
      setFormData(prev => ({
        ...prev,
        driver_id: driverId,
        employee_name: selectedDriver.name,
        pay_number: selectedDriver.employee_id || ''
      }));
      setDriverSearch(selectedDriver.name);
      setShowDriverDropdown(false);
    }
  };

  const filteredDrivers = drivers.filter(driver =>
    driver.name.toLowerCase().includes(driverSearch.toLowerCase()) ||
    (driver.employee_id && driver.employee_id.toLowerCase().includes(driverSearch.toLowerCase()))
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const startedAt = new Date(report.started_at);
      const oneHourLater = new Date(startedAt.getTime() + 60 * 60 * 1000);
      const now = new Date();
      const diff = oneHourLater - now;

      if (diff <= 0) {
        setIsOverdue(true);
        setTimeRemaining('OVERDUE');
      } else {
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [report]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await axios.put(
        `${API}/vcr/${report.id}/stage1`,
        formData,
        getAuthHeaders()
      );
      toast.success('Progress saved');
      if (onSave) onSave();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${API}/vcr/${report.id}/submit-stage1`,
        { ...formData, is_overdue: isOverdue },
        getAuthHeaders()
      );
      
      // Generate email via mailto link
      const emailSubject = isOverdue 
        ? `[OVERDUE] VCR Stage 1 - ${formData.employee_name} - ${new Date().toLocaleString()}`
        : `VCR Stage 1 - ${formData.employee_name} - ${new Date().toLocaleString()}`;
      
      const emailBody = `Vehicle Collision Report - Stage 1 Completed

Employee: ${formData.employee_name}
Pay Number: ${formData.pay_number}
Incident Date: ${formData.incident_date}
Incident Time: ${formData.incident_time}
Investigating Manager: ${formData.investigating_manager}

Collision Type: ${formData.initial_collision_type}
What Damaged: ${formData.what_damaged}
Manoeuvre: ${formData.manoeuvre}
Location: ${formData.location_name}
Location Type: ${formData.location_type}

Weather: ${formData.weather_conditions}
Light Conditions: ${formData.light_conditions}

Initial Statement:
${formData.initial_statement}

---
This is Stage 1 of 4. Stage 2 must be completed within 24 hours.
VCR ID: ${report.id}`;

      const recipients = 'david.l.mills@royalmail.com,richard.ohalloran@royalmail.com,simon.ashley@royalmail.com,chris.s.palmer@royalmail.com,adam.moat@royalmail.com,james.sangwine@royalmail.com,john.b.clark1@royalmail.com,david.spittle@royalmail.com,kola.akindiya@royalmail.com,balaji.jeyaprakasam@royalmail.com,andrew.r.gardner@royalmail.com';
      const mailtoLink = `mailto:${recipients}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      window.location.href = mailtoLink;
      
      toast.success('Stage 1 submitted - Email client opened');
      
      // Delay navigation to allow mailto to trigger
      setTimeout(() => {
        if (onNext) onNext();
      }, 1000);
    } catch (error) {
      toast.error('Failed to submit stage 1');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Timer */}
      <Card className={isOverdue ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className={isOverdue ? 'h-5 w-5 text-red-600' : 'h-5 w-5 text-amber-600'} />
              <span className="font-medium">{isOverdue ? 'DEADLINE PASSED' : 'Time Remaining'}</span>
            </div>
            <span className={`text-2xl font-bold ${isOverdue ? 'text-red-600' : 'text-amber-600'}`}>
              {timeRemaining}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* ERICA Button */}
      <Card className="border-blue-300 bg-blue-50 dark:bg-blue-900/20">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Royal Mail ERICA System</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Open ERICA to file parallel report</p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => window.open('http://intranet.royalmailgroup.net/erica/Default.aspx', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Open ERICA
            </Button>
          </div>
        </CardContent>
      </Card>


      <Card className="border-red-300">
        <CardHeader>
          <CardTitle className="text-red-600">Stage 1: Initial Report (1st Hour)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Incident Date *</Label>
              <Input
                type="date"
                value={formData.incident_date}
                onChange={(e) => handleChange('incident_date', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Incident Time *</Label>
              <Input
                type="time"
                value={formData.incident_time}
                onChange={(e) => handleChange('incident_time', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 relative">
              <Label>Select Driver (Type to search) *</Label>
              <Input
                type="text"
                value={driverSearch}
                onChange={(e) => {
                  setDriverSearch(e.target.value);
                  setShowDriverDropdown(true);
                }}
                onFocus={() => setShowDriverDropdown(true)}
                placeholder="Start typing driver name or ID..."
                required={!formData.driver_id}
              />
              
              {showDriverDropdown && filteredDrivers.length > 0 && (
                <div ref={dropdownRef} className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredDrivers.slice(0, 10).map((driver) => (
                    <div
                      key={driver.id}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b"
                      onClick={() => handleDriverChange(driver.id)}
                    >
                      <p className="font-medium text-slate-800">{driver.name}</p>
                      {driver.employee_id && (
                        <p className="text-sm text-slate-600">ID: {driver.employee_id}</p>
                      )}
                    </div>
                  ))}
                  {filteredDrivers.length > 10 && (
                    <div className="px-4 py-2 text-sm text-slate-500">
                      {filteredDrivers.length - 10} more... Keep typing to narrow results
                    </div>
                  )}
                </div>
              )}
              
              {formData.employee_name && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Selected: {formData.employee_name}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Pay Number (Auto-filled)</Label>
              <Input
                value={formData.pay_number}
                onChange={(e) => handleChange('pay_number', e.target.value)}
                placeholder="Auto-populated from driver"
                readOnly
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Investigating Manager *</Label>
              <Input
                value={formData.investigating_manager}
                onChange={(e) => handleChange('investigating_manager', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Date First Reported *</Label>
              <Input
                type="date"
                value={formData.date_first_reported}
                onChange={(e) => handleChange('date_first_reported', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Driver Type</Label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={formData.driver_type}
                onChange={(e) => handleChange('driver_type', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="Driver">Driver</option>
                <option value="Driver Manager">Driver Manager</option>
                <option value="Relief">Relief</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Injury/Harm</Label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={formData.injury_harm}
                onChange={(e) => handleChange('injury_harm', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="None">None</option>
                <option value="Minor">Minor</option>
                <option value="Serious">Serious</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Attention Received</Label>
              <Input
                value={formData.attention_received}
                onChange={(e) => handleChange('attention_received', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Initial Collision Type *</Label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={formData.initial_collision_type}
                onChange={(e) => handleChange('initial_collision_type', e.target.value)}
                required
              >
                <option value="">Select...</option>
                <option value="Collision with vehicle">Collision with vehicle</option>
                <option value="Collision with object">Collision with object</option>
                <option value="Collision with pedestrian">Collision with pedestrian</option>
                <option value="Single vehicle">Single vehicle</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>What Damaged *</Label>
              <Input
                value={formData.what_damaged}
                onChange={(e) => handleChange('what_damaged', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Manoeuvre *</Label>
              <Input
                value={formData.manoeuvre}
                onChange={(e) => handleChange('manoeuvre', e.target.value)}
                placeholder="e.g., Reversing, Turning, Parking"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Vehicle Type</Label>
              <Input
                value={formData.vehicle_type}
                onChange={(e) => handleChange('vehicle_type', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Location Name *</Label>
              <Input
                value={formData.location_name}
                onChange={(e) => handleChange('location_name', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Location Type</Label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={formData.location_type}
                onChange={(e) => handleChange('location_type', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="Depot">Depot</option>
                <option value="Delivery Office">Delivery Office</option>
                <option value="Public Road">Public Road</option>
                <option value="Car Park">Car Park</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Weather Conditions</Label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={formData.weather_conditions}
                onChange={(e) => handleChange('weather_conditions', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="Clear">Clear</option>
                <option value="Rain">Rain</option>
                <option value="Snow">Snow</option>
                <option value="Fog">Fog</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Light Conditions</Label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={formData.light_conditions}
                onChange={(e) => handleChange('light_conditions', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="Daylight">Daylight</option>
                <option value="Dusk/Dawn">Dusk/Dawn</option>
                <option value="Darkness">Darkness</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Initial Information/Statement *</Label>
            <Textarea
              value={formData.initial_statement}
              onChange={(e) => handleChange('initial_statement', e.target.value)}
              rows={6}
              placeholder="Provide detailed initial account of the incident..."
              required
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={handleSave}>
          <Save className="h-4 w-4 mr-2" />
          Save Progress
        </Button>
        <Button type="submit" className="bg-red-600 hover:bg-red-700">
          <Send className="h-4 w-4 mr-2" />
          Submit Stage 1 & Send Email
        </Button>
      </div>
    </form>
  );
}
